import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Tooltip,
  AlertTitle,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Replay as RetryIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as ViewIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Update as UpdateIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import Navbar from '@/components/home/Navbar';

import knowledgeBaseService, {
  KnowledgeBase,
  KnowledgeDocument,
} from '@/services/knowledgeBaseService';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

interface StatusChipProps {
  status: string;
}

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})<StatusChipProps>(({ theme, status }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'grey';
      case 'pending':
        return 'info';
      case 'pending_update':
        return 'warning';
      case 'building':
      case 'processing':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'grey';
    }
  };

  const colorKey = getStatusColor(status);
  const palette = theme.palette as any;

  return {
    backgroundColor: palette[colorKey]?.main || palette.grey.main,
    color: palette[colorKey]?.contrastText || palette.grey.contrastText,
    fontWeight: 'bold',
  };
});

const KnowledgeBaseDetailPage: React.FC = () => {
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();
  const navigate = useNavigate();
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, documentId: 0, documentName: '' });

  // 获取知识库详情
  const fetchKnowledgeBaseDetail = async () => {
    if (!knowledgeBaseId) return;

    try {
      const response = await knowledgeBaseService.getKnowledgeBase(parseInt(knowledgeBaseId));
      setKnowledgeBase(response.data);
    } catch (error: any) {
      setSnackbar({ open: true, message: '获取知识库详情失败', severity: 'error' });
    }
  };

  // 获取文档列表
  const fetchDocuments = async () => {
    if (!knowledgeBaseId) return;

    try {
      const response = await knowledgeBaseService.getKnowledgeBaseDocuments(parseInt(knowledgeBaseId));
      setDocuments(response.data.documents);
    } catch (error: any) {
    }
  };

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      await Promise.all([
        fetchKnowledgeBaseDetail(),
        fetchDocuments(),
      ]);
      setLoading(false);
    };

    initData();

    // 监听全局文档刷新事件
    const handleGlobalRefresh = () => {
      fetchDocuments();
    };

    window.addEventListener('documentsShouldRefresh', handleGlobalRefresh);

    return () => {
      window.removeEventListener('documentsShouldRefresh', handleGlobalRefresh);
    };
  }, [knowledgeBaseId]);

  // 处理文档上传（支持多文件）
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !knowledgeBaseId) return;

    // 验证文件类型
    const allowedTypes = [
      '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.md'
    ];

    const invalidFiles = files.filter(file => {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      return !allowedTypes.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      setSnackbar({
        open: true,
        message: `${invalidFiles.length} 个文件类型不支持，请上传 PDF、Word、Excel、PowerPoint 或文本文件`,
        severity: 'error',
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    let successCount = 0;
    let failCount = 0;

    try {
      // 逐个上传文件
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const progress = Math.round(((i) / files.length) * 100);
        setUploadProgress(progress);

        try {
          await knowledgeBaseService.uploadDocument(
            parseInt(knowledgeBaseId),
            file,
            () => {}  // 忽略单个文件的进度回调
          );
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      setUploadProgress(100);

      // 显示结果
      if (successCount > 0 && failCount === 0) {
        setSnackbar({
          open: true,
          message: `成功上传 ${successCount} 个文档，正在后台处理`,
          severity: 'success',
        });
      } else if (successCount > 0) {
        setSnackbar({
          open: true,
          message: `成功上传 ${successCount} 个文档，失败 ${failCount} 个`,
          severity: 'warning',
        });
      } else {
        setSnackbar({
          open: true,
          message: '所有文件上传失败',
          severity: 'error',
        });
      }

      // 刷新数据
      await fetchDocuments();

      // 刷新知识库详情
      await fetchKnowledgeBaseDetail();

    } catch (error: any) {
      setSnackbar({
        open: true,
        message: '文档上传失败',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }

    // 清空文件输入
    if (event.target) {
      event.target.value = '';
    }
  };

  // 创建手动任务
  const handleCreateManualTask = async (
    taskType: 'document_conversion' | 'dataset_reconstruction',
    documentId?: number
  ) => {
    if (!knowledgeBaseId) return;

    try {
      await knowledgeBaseService.createManualTask(parseInt(knowledgeBaseId), taskType, documentId);
      setSnackbar({
        open: true,
        message: `手动${taskType === 'dataset_reconstruction' ? '重构数据集' : '转换文档'}任务已创建`,
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: '创建手动任务失败',
        severity: 'error',
      });
    }
  };

  // 处理删除文档
  const handleDeleteDocument = async (documentId: number, documentName: string) => {
    if (!knowledgeBaseId) return;

    try {
      await knowledgeBaseService.deleteDocument(parseInt(knowledgeBaseId), documentId);
      setSnackbar({
        open: true,
        message: '文档删除成功',
        severity: 'success',
      });

      // 刷新文档列表和知识库详情
      await Promise.all([fetchDocuments(), fetchKnowledgeBaseDetail()]);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || '删除文档失败',
        severity: 'error',
      });
    } finally {
      setDeleteDialog({ open: false, documentId: 0, documentName: '' });
    }
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      not_started: '未开始',
      pending_update: '待更新',
      building: '构建中',
      completed: '已完成',
      failed: '失败',
      pending: '待处理',
      processing: '处理中',
      new: '新建',
    };
    return statusMap[status] || status;
  };

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'not_started':
        return <HourglassEmptyIcon />;
      case 'pending_update':
        return <UpdateIcon />;
      case 'building':
        return <RefreshIcon className="animate-spin" />;
      case 'completed':
        return <CheckCircleIcon />;
      case 'failed':
        return <ErrorIcon />;
      case 'pending':
        return <HourglassEmptyIcon />;
      case 'processing':
        return <RefreshIcon className="animate-spin" />;
      default:
        return null;
    }
  };

  // 格式化时间
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 格式化文件大小
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          加载中...
        </Typography>
      </Box>
    );
  }

  if (!knowledgeBase) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <AlertTitle>错误</AlertTitle>
          知识库不存在或已被删除
        </Alert>
      </Box>
    );
  }

  return (
    <>
      {/* 导航栏 */}
      <Navbar />

      {/* 页面内容 */}
      <Box p={3}>
        {/* 页面头部 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => navigate('/knowledge-base')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              {knowledgeBase.name}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => Promise.all([fetchKnowledgeBaseDetail(), fetchDocuments()])}
              sx={{ mr: 2 }}
            >
              刷新
            </Button>
          </Box>
        </Box>

      {/* 知识库信息卡片 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            知识库信息
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                数据集名称
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {knowledgeBase.dataset_name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                构建状态
              </Typography>
              <StatusChip
                label={getStatusText(knowledgeBase.build_status)}
                status={knowledgeBase.build_status}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                文档数量
              </Typography>
              <Typography variant="body1">
                {knowledgeBase.document_count}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                启用状态
              </Typography>
              <Chip
                label={knowledgeBase.is_enabled ? '启用' : '未启用'}
                color={knowledgeBase.is_enabled ? 'success' : 'default'}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                描述
              </Typography>
              <Typography variant="body1">
                {knowledgeBase.description || '暂无描述'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 文档管理 */}
      <Card>
          <CardContent>
            {/* 上传区域 */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                上传文档
              </Typography>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                    borderColor: '#999',
                  },
                }}
              >
                <input
                  type="file"
                  id="document-upload"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
                <label htmlFor="document-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={uploading}
                  >
                    {uploading ? '上传中...' : '选择文件（可多选）'}
                  </Button>
                </label>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  支持的文件格式：PDF、Word、Excel、PowerPoint、TXT、MD（可同时选择多个文件）
                </Typography>

                {uploading && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      上传进度: {uploadProgress}%
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* 手动操作按钮 */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                手动操作
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => handleCreateManualTask('dataset_reconstruction')}
                sx={{ mr: 2 }}
              >
                重构数据集
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* 文档列表 */}
            <Typography variant="h6" gutterBottom>
              文档列表 ({documents.length})
            </Typography>
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>文件名</TableCell>
                    <TableCell>文件大小</TableCell>
                    <TableCell>转换状态</TableCell>
                    <TableCell>Cognee状态</TableCell>
                    <TableCell>上传时间</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          暂无文档
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    documents.map((doc) => (
                      <StyledTableRow key={doc.id}>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {doc.original_filename}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatFileSize(doc.file_size)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(doc.conversion_status)}
                            <StatusChip
                              label={getStatusText(doc.conversion_status)}
                              status={doc.conversion_status}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(doc.cognee_status)}
                            <StatusChip
                              label={getStatusText(doc.cognee_status)}
                              status={doc.cognee_status}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(doc.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="重试转换">
                            <IconButton
                              size="small"
                              onClick={() => doc.id && handleCreateManualTask('document_conversion', doc.id)}
                              disabled={doc.conversion_status === 'processing'}
                            >
                              <RetryIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="删除文档">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({
                                open: true,
                                documentId: doc.id || 0,
                                documentName: doc.original_filename
                              })}
                              disabled={doc.conversion_status === 'processing'}
                              sx={{ ml: 1 }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, documentId: 0, documentName: '' })}
      >
        <DialogTitle>确认删除文档</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除文档 <strong>{deleteDialog.documentName}</strong> 吗？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            此操作将删除文档及其转换后的文件，无法恢复。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, documentId: 0, documentName: '' })}
          >
            取消
          </Button>
          <Button
            onClick={() => handleDeleteDocument(deleteDialog.documentId, deleteDialog.documentName)}
            color="error"
            variant="contained"
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </>
  );
};

export default KnowledgeBaseDetailPage;